<script setup lang="ts">
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useLogto } from '@logto/vue';
import { session } from '../lib/session.ts';
import { t } from '../lib/copy.ts';
import DevSignIn from './DevSignIn.vue';

const route = useRoute();

interface NavItem { to: string; label: string; show: () => boolean }

// Nav is built from what the account can do, which the API told us. A hidden
// link is a convenience; the API returns 403 regardless.
const items: NavItem[] = [
  { to: '/my/applications', label: t('nav.myApplications'), show: () => true },
  { to: '/judge',  label: t('nav.judging'),  show: () => session.capabilities.judgePanelIds.length > 0 },
  { to: '/review', label: t('nav.review'),   show: () => session.capabilities.isConductor },
  { to: '/team/dashboard', label: t('nav.dashboard'), show: () => session.capabilities.isTeam },
  { to: '/team/mail', label: t('nav.mail'), show: () => session.capabilities.isTeam },
  { to: '/team/applicants', label: t('nav.applicants'), show: () => session.capabilities.isTeam },
  { to: '/team/videos', label: t('nav.videos'), show: () => session.capabilities.isTeam },
  { to: '/team/setup', label: t('nav.setup'), show: () => session.capabilities.isTeam },
  { to: '/team/adjudicators', label: t('nav.adjudicators'), show: () => session.capabilities.isTeam },
  { to: '/team/wording', label: t('nav.wording'), show: () => session.capabilities.isTeam },
  { to: '/team/principals', label: t('nav.principals'), show: () => session.capabilities.isTeam },
  { to: '/team/release', label: t('nav.release'), show: () => session.capabilities.isTeam },
  { to: '/team/master', label: t('nav.masterSheet'), show: () => session.capabilities.isTeam },
];

const visible = computed(() => items.filter((i) => i.show()));
const year = new Date().getFullYear();

const { signIn, signOut } = useLogto();

/*
 * Signing out goes back to the front page, which is the one route that says
 * anything useful to somebody with no account. The address is registered with
 * Logto as a post-logout redirect; an unregistered one is refused and the
 * person is left on the identity provider's own page wondering what happened.
 */
const homeUrl = `${window.location.origin}/`;

/*
 * A page that needs an account shows a sign-in prompt rather than its content.
 * Gated on authReady, so nothing is decided before the answer exists - the
 * redirect this replaces fired too early and bounced signed-in people home.
 * The API returns 401 or 403 either way; this is only what a person sees.
 */
const needsSignIn = computed(() =>
  !route.meta.public && session.authReady && !session.isAuthenticated);

const callbackUrl = `${window.location.origin}/callback`;
</script>

<template>
  <!-- The first focusable element on the page. -->
  <a class="skip-link" href="#main">{{ t('shell.skipLink') }}</a>

  <header class="masthead">
    <div class="shell masthead__inner">
      <span class="display masthead__brand">{{ t('shell.brand') }}</span>
      <span class="masthead__year">{{ t('shell.yearLabel', { year }) }}</span>
      <nav class="masthead__nav" :aria-label="t('shell.navLabel')">
        <RouterLink
          v-for="item in visible"
          :key="item.to"
          class="masthead__link"
          :to="item.to"
          :aria-current="route.path.startsWith(item.to) ? 'page' : undefined"
        >{{ item.label }}</RouterLink>
        <!-- Only once there is somebody to sign out. Before that the answer is
             not known, and a button that appears a moment later is worse than
             one that was never there. -->
        <button
          v-if="session.authReady && session.isAuthenticated"
          class="masthead__link masthead__signout"
          type="button"
          @click="signOut(homeUrl)"
        >{{ t('shell.signOut') }}</button>
      </nav>
    </div>
  </header>

  <main id="main" class="shell" tabindex="-1">
    <p v-if="session.error" class="field-error" role="alert">{{ t('shell.capabilitiesError') }}</p>

    <section v-if="needsSignIn" class="empty">
      <p>{{ t('auth.needSignIn') }}</p>
      <p style="margin-top: var(--s-4)">
        <button class="btn-primary" @click="signIn(callbackUrl)">{{ t('auth.signIn') }}</button>
      </p>
      <DevSignIn />
    </section>
    <slot v-else />
  </main>

  <footer class="site-footer">
    <div class="shell">
      <!-- The provisional-rankings line lives on views that show a ranking,
           not here: on the signed-out home page it says nothing to anybody.
           See components/ProvisionalNote.vue. -->
      <p>
        {{ t('shell.footerOrg') }} &middot;
        <a class="link" :href="t('shell.privacyUrl')">{{ t('shell.privacyNotice') }}</a> &middot;
        <RouterLink class="link" to="/terms">{{ t('shell.terms') }}</RouterLink> &middot;
        <RouterLink class="link" to="/help">{{ t('shell.help') }}</RouterLink>
      </p>
    </div>
  </footer>
</template>
